<template>
  <div class="p-8 space-y-8">
    <BaseCard>
      <BaseCardBody>
        <div class="flex justify-between">
          <h3 class="font-bold text-lg">Marketing dashboard</h3>
          <div class="flex items-center gap-3">
            <BaseDatePicker v-model="range" :default-label="datePickerLabel">
              <template #default="{ shown }">
                <div
                  class="select-none rounded-md border border-gray-300 px-3 py-2 cursor-pointer"
                  :class="[shown ? '' : 'hover:bg-gray-100 hover:border-blue-600']"
                >
                  <div class="flex gap-3 items-center">
                    <div class="flex-1">
                      <span for="name" class="block text-xs font-medium text-gray-900 text-right">{{
                        $t('global.period')
                      }}</span>
                      <span
                        v-text="datePickerLabel"
                        class="block w-full border-0 p-0 text-gray-900 placeholder-gray-500 sm:text-sm"
                      />
                    </div>
                    <div class="flex-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </template>
            </BaseDatePicker>
          </div>
        </div>
      </BaseCardBody>
      <BaseCardBody v-if="$route.params.view" clear-padding>
        <Tabs :key="$route.params.view" :tabs="tabs" />
      </BaseCardBody>
    </BaseCard>
    <RouterView />
  </div>
</template>

<script>
import moment from 'moment';
import Tabs from './components/Tabs.vue';

const findDefaultView = (tabs) => {
  for (const tab of tabs) {
    if ('view' in tab) {
      return tab.view;
    }

    const view = findDefaultView(tab.children || []);

    if (view) {
      return view;
    }
  }

  return null;
};

const findViewByName = (tabs, name) => {
  if (!tabs.length) {
    return null;
  }

  for (const tab of tabs) {
    if (tab.name === name) {
      return tab;
    }

    const child = findViewByName(tab?.children || [], name);

    if (child) {
      return child;
    }
  }

  return null;
};

export default {
  components: {
    Tabs,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    view: {
      type: String,
      default: '',
    },
  },
  computed: {
    tabs() {
      return [
        {
          name: 'tactics',
          label: 'Tactics',
          view: 'tactics',
        },
        {
          name: 'posts',
          label: 'Posts',
          children: [
            {
              name: 'facebook-posts',
              label: 'Facebook',
              view: 'facebook-posts',
            },
            {
              name: 'instagram-posts',
              label: 'Instagram',
              view: 'instagram-posts',
            },
            {
              name: 'youtube-posts',
              label: 'Youtube',
              view: 'youtube-posts',
            },
            {
              name: 'blog-posts',
              label: 'Blog',
              view: 'blog-posts',
            },
          ],
        },
        {
          name: 'bulk-messaging',
          label: 'Bulk messaging',
          children: [
            {
              name: 'email-campaigns',
              label: 'Email',
              view: 'email-campaigns',
            },
            {
              name: 'sms-campaigns',
              label: 'SMS',
              view: 'sms-campaigns',
            },
          ],
        },
        {
          name: 'ads',
          label: 'Ads',
          children: [
            {
              name: 'facebook-ad-posts',
              label: 'Facebook',
              view: 'facebook-ad-posts',
            },
            {
              name: 'instagram-ad-posts',
              label: 'Instagram',
              view: 'instagram-ad-posts',
            },
            {
              name: 'google-ads',
              label: 'Google',
              view: 'google-ads',
            },
            {
              name: 'youtube-ads',
              label: 'Youtube',
              view: 'youtube-ads',
            },
          ],
        },
        {
          name: 'search',
          label: 'Search',
          view: 'search',
        },
        {
          name: 'facebook-campaigns',
          label: 'Facebook campaigns',
          view: 'facebook-campaigns',
        },
      ];
    },
    range: {
      get() {
        const { since, until } = this.$route.params;
        if (since && until) {
          return [since, until];
        }

        return [];
      },
      set([since, until]) {
        if (since === this.$route.params.since && until === this.$route.params.until) {
          return;
        }

        if (!since) {
          this.$router.replace({
            name: 'accounts.accountId.marketingDashboard',
            params: {
              ...this.$route.params,
              since,
              until,
            },
          });
          return;
        }

        this.$router.replace({
          name: 'accounts.accountId.marketingDashboard.rangedView',
          params: {
            ...this.$route.params,
            since,
            until,
          },
        });
      },
    },
    datePickerLabel() {
      if (this.range.length === 0) {
        return `${this.$t('global.beginOfTimeUntil')} ${moment().format('DD-MMM-YYYY')}`;
      }

      const [since, until] = this.range.map((date) => moment(date));

      if (since.isSame(until)) {
        return since.format('DD-MMM-YYYY');
      }

      return [
        this.$t('global.periodFrom'),
        since.format('DD-MMM-YYYY'),
        this.$t('global.periodΤο'),
        until.format('DD-MMM-YYYY'),
      ].join(' ');
    },
  },
  created() {
    const matchedView = findViewByName(this.tabs, this.view);

    if (matchedView) {
      return;
    }

    const defaultView = findDefaultView(this.tabs);

    this.$router.replace({
      name: 'accounts.accountId.marketingDashboard',
      params: {
        ...this.$route.params,
        view: defaultView,
      },
    });
  },
  beforeRouteUpdate(to, from, next) {
    const matchedView = findViewByName(this.tabs, to.params.view);

    if (matchedView) {
      return next();
    }

    const defaultView = findDefaultView(this.tabs);

    next({
      name: this.$route.name,
      params: {
        ...this.$route.params,
        view: defaultView,
      },
    });
  },
};
</script>
